import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

export const ModDataApi = createApi({
  reducerPath: "ModData",
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_BACKEND2_URL,
    prepareHeaders: (headers) => {
      const token = localStorage.getItem("token");
      if (token) {
        headers.set("Authorization", `Bearer ${token}`);
      }
      return headers;
    },
  }),
  endpoints: (builder) => ({
    getModData: builder.query({
      query: (params = {}) => {
        const { search, groupName } = params || {};
        let url = "/v1/mdvrs/group-view";
        const queryParams = new URLSearchParams();

        if (search && search.trim()) {
          queryParams.append("search", search.trim());
        }
        if (groupName) {
          queryParams.append("groupName", groupName);
        }

        const queryString = queryParams.toString();
        return queryString ? `${url}?${queryString}` : url;
      },
      // Ensure RTK Query treats different parameter combinations as different queries
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        // Serialize query args to ensure proper cache key
        const { search = "", groupName = "" } = queryArgs || {};
        return `${endpointName}(${JSON.stringify({ search, groupName })})`;
      },
      transformResponse: (response) => {
        // Transform tree structure into flat arrays for groups and devices
        const groups = [];
        const devices = [];

        if (response?.data && Array.isArray(response.data)) {
          response.data.forEach((group) => {
            // Add group to groups array
            let attributes = {};
            try {
              attributes = group.attributes ? JSON.parse(group.attributes) : {};
            } catch (e) {
              console.error("Error parsing group attributes:", e);
            }

            groups.push({
              id: group.id,
              name: group.name,
              deviceCount: group.deviceCount || 0,
              attributes: attributes,
            });

            // Add devices from this group
            if (group.devices && Array.isArray(group.devices)) {
              group.devices.forEach((device) => {
                const mdvr = device.mdvr || {};
                const channels = mdvr.channels || [];

                devices.push({
                  id: device.id,
                  name: device.name,
                  uniqueId: device.uniqueid,
                  status: device.status || "offline",
                  groupId: group.id,
                  mdvr: {
                    id: mdvr.id,
                    model: mdvr.model,
                    status: mdvr.status,
                    channelCount: mdvr.channelCount || 0,
                    lastOnlineAt: mdvr.lastOnlineAt,
                    channels: channels.map((channel) => ({
                      id: channel.id,
                      channelNumber: channel.channelNumber,
                      name: channel.name,
                      position: channel.position,
                      isActive: channel.isActive,
                    })),
                    activeStreams: mdvr.activeStreams || 0,
                  },
                });
              });
            }
          });
        }

        return {
          groups,
          devices,
          results: response?.results || 0,
          status: response?.status || "success",
        };
      },
    }),
    startStream: builder.mutation({
      queryFn: async (body, _queryApi, _extraOptions, baseQuery) => {
        const token = localStorage.getItem("token");
        const headers = {
          "Content-Type": "application/json",
        };
        if (token) {
          headers["Authorization"] = `Bearer ${token}`;
        }

        try {
          const response = await fetch(
            "https://fom.softsprint.ae/media/api/mdvr/stream/start",
            {
              method: "POST",
              headers,
              body: JSON.stringify(body),
            },
          );

          if (!response.ok) {
            return {
              error: { status: response.status, data: await response.text() },
            };
          }

          const data = await response.json();
          return { data };
        } catch (error) {
          return { error: { status: "FETCH_ERROR", error: error.message } };
        }
      },
    }),
    stopStream: builder.mutation({
      queryFn: async (body, _queryApi, _extraOptions, baseQuery) => {
        const token = localStorage.getItem("token");
        const headers = {
          "Content-Type": "application/json",
        };
        if (token) {
          headers["Authorization"] = `Bearer ${token}`;
        }

        try {
          const response = await fetch(
            "https://fom.softsprint.ae/media/api/mdvr/stream/stop",
            {
              method: "POST",
              headers,
              body: JSON.stringify(body),
            },
          );

          if (!response.ok) {
            return {
              error: { status: response.status, data: await response.text() },
            };
          }

          const data = await response.json();
          return { data };
        } catch (error) {
          return { error: { status: "FETCH_ERROR", error: error.message } };
        }
      },
    }),
    getHistorySegments: builder.query({
      queryFn: async (
        {
          imei,
          startTime,
          endTime,
          channel,
          streamType = 0,
          alarmOnly = false,
        },
        _queryApi,
        _extraOptions,
        baseQuery,
      ) => {
        const token = localStorage.getItem("token");
        const headers = {};
        if (token) {
          headers["Authorization"] = `Bearer ${token}`;
        }

        try {
          const params = new URLSearchParams({
            imei,
            startTime,
            endTime,
            channel: channel.toString(),
            streamType: streamType.toString(),
            alarmOnly: alarmOnly.toString(),
          });

          const response = await fetch(
            `https://fom.softsprint.ae/media/api/mdvr/videos/files/range?${params.toString()}`,
            {
              method: "GET",
              headers,
            },
          );

          if (!response.ok) {
            return {
              error: { status: response.status, data: await response.text() },
            };
          }

          const data = await response.json();
          return { data };
        } catch (error) {
          return { error: { status: "FETCH_ERROR", error: error.message } };
        }
      },
    }),
    startPlaybackHLS: builder.mutation({
      queryFn: async (body, _queryApi, _extraOptions, baseQuery) => {
        const token = localStorage.getItem("token");
        const headers = {
          "Content-Type": "application/json",
        };
        if (token) {
          headers["Authorization"] = `Bearer ${token}`;
        }

        try {
          const response = await fetch(
            "https://fom.softsprint.ae/media/api/mdvr/playback/start",
            {
              method: "POST",
              headers,
              body: JSON.stringify(body),
            },
          );

          if (!response.ok) {
            return {
              error: { status: response.status, data: await response.text() },
            };
          }

          const data = await response.json();
          return { data };
        } catch (error) {
          return { error: { status: "FETCH_ERROR", error: error.message } };
        }
      },
    }),
  }),
});

export const {
  useGetModDataQuery,
  useLazyGetModDataQuery,
  useStartStreamMutation,
  useStopStreamMutation,
  useGetHistorySegmentsQuery,
  useStartPlaybackHLSMutation,
} = ModDataApi;
